import { useEffect, useState, useRef } from "react";
import { parseDate, fetchData } from "../../../../functions/functions";
import SportContentHomeRender from "./sportContentHomeRender";
import EmptyState from "../emptyState/emptyState";
import Loading from "../../../loading/loading";

/**
 * Componente que renderiza el contenido de cada tab en el hub de resultados
 * Maneja la visualización de deportes y agenda según fechas específicas
 * Incluye carga bajo demanda con cache
 */
export default function TabContent({ 
  data, 
  index, 
  pathIframe, 
  mostrarBotonApostar, 
  mostrarMomios, 
  patrocinador,
  endpoint,
  isActive,
  cachedData,
  onDataLoaded,
  selectedSport,
  selectedLeague,
  isLiveOnly
}) {
  const [loading, setLoading] = useState(false);
  const [tabData, setTabData] = useState(data || cachedData || null);
  const hasLoadedRef = useRef(false);
  const abortControllerRef = useRef(null);

  /**
   * Filtra los deportes y ligas según los filtros activos
   * @param {Object} sportsData - Datos de deportes
   * @returns {Object} Datos filtrados
   */
  const filterGames = (sportsData) => {
    if (!sportsData || !sportsData.sports) return sportsData;

    let filteredSports = sportsData.sports;

    // Filtrar por deporte si está seleccionado
    if (selectedSport) {
      filteredSports = filteredSports.filter(sport => sport.sport_code === selectedSport);
    }

    // Filtrar por liga si está seleccionada (solo si hay deporte seleccionado)
    if (selectedLeague && selectedSport) {
      filteredSports = filteredSports
        .map(sport => ({
          ...sport,
          leagues: sport.leagues.filter(league => league.name === selectedLeague)
        }))
        .filter(sport => sport.leagues.length > 0);
    }

    // Filtrar por juegos NO en vivo por defecto, o solo en vivo si se activa el filtro
    if (isLiveOnly) {
       filteredSports = filteredSports
        .map(sport => ({
          ...sport,
          leagues: sport.leagues
            .map(league => ({
              ...league,
              games: league.games.filter(game => game.class === 'clarosports-in-progress-game')
            }))
            .filter(league => league.games.length > 0)
        }))
        .filter(sport => sport.leagues.length > 0);
    }

    return {
      ...sportsData,
      sports: filteredSports
    };
  };

  // Cargar datos cuando la tab se activa
  useEffect(() => {
    // Si ya tiene datos (del prop data), usarlos
    if (data) {
      setTabData(data);
      hasLoadedRef.current = true;
      return;
    }

    // Si data es null, limpiar tabData (no usar cachedData)
    if (data === null && hasLoadedRef.current) {
      setTabData(null);
      hasLoadedRef.current = false;
      return;
    }

    // Si no está activa, no cargar
    if (!isActive || !endpoint) {
      return;
    }

    // Si ya se cargó, no hacer nada
    if (hasLoadedRef.current) {
      return;
    }

    // Cancelar petición anterior si existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Crear nuevo AbortController para esta petición
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const loadTabData = async () => {
      setLoading(true);
      
      try {
        const response = await fetchData(endpoint);
        
        // Verificar si la petición fue cancelada
        if (signal.aborted) return;

        // // // Importar las variables necesarias
        // const {TEN, BASE, F1 } = await import('../../../../functions/functions');

        // // Agregar variables a la respuesta
        // if (response) {

        //   // Concatenar TEN al final del array de sports
        //   if (response.sports && Array.isArray(response.sports)) {
        //     response.sports.push(TEN);
        //     response.sports.push(BASE);
        //      response.sports.push(F1);
        //   }
        //   console.log(" response: ", response)
        // }
        
        setTabData(response);
        onDataLoaded?.(response);
        hasLoadedRef.current = true;
      } catch (error) {
        // Si fue cancelada, no hacer nada
        if (signal.aborted) return;
        
        // Intentar con fallback
        try {
          const { getApiEndpoints } = await import('../../../../constantes/constantes');
          const dynamicEndpoints = getApiEndpoints();
          const fallbackUrl = dynamicEndpoints[index]?.fallback;
          
          if (fallbackUrl && !signal.aborted) {
            const fallbackResponse = await fetchData(fallbackUrl);
            
            if (signal.aborted) return;
            
            setTabData(fallbackResponse);
            onDataLoaded?.(fallbackResponse);
            hasLoadedRef.current = true;
          }
        } catch (fallbackError) {
          if (!signal.aborted) {
            console.error(`Error con fallback para tab ${index}:`, fallbackError);
          }
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadTabData();

    // Cleanup: cancelar petición si el componente se desmonta o cambia
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isActive, endpoint, index]);
  
  // Efecto separado para actualizar tabData cuando cambia el prop data
  useEffect(() => {
    if (data && hasLoadedRef.current) {
      setTabData(data);
    }
  }, [data]);
  
  // Mostrar loading si está cargando
  if (loading) {
    return <Loading type="150px" />;
  }

  // Filtrar datos según los filtros activos
  const filteredTabData = filterGames(tabData);

  // Si hay un deporte seleccionado pero no hay datos filtrados, mostrar EmptyState
  if (selectedSport && (!filteredTabData || !filteredTabData.sports?.length)) {
    return <EmptyState selectedSport={selectedSport} filteredData={filteredTabData} isActive={isActive} />;
  }

  // Validación temprana: no renderizar si no hay datos o deportes
  if (!filteredTabData || !filteredTabData.sports?.length) return null;

  // Configuración de fechas para mostrar la agenda (período específico)
  const comparisonDate2 = parseDate("24/07/2024");
  const comparisonDateEnd = parseDate("12/08/2024");
  
  /**
   * Determina si debe mostrar la agenda basado en el rango de fechas
   * Solo se muestra durante eventos especiales (ej: Juegos Olímpicos)
   */
  const showAgenda = parseDate(filteredTabData.date) >= comparisonDate2 && 
                     parseDate(filteredTabData.date) < comparisonDateEnd;

  return (
    <>
      {/* Agenda especial para fechas específicas */}
      {showAgenda && (
        <div className="agenda-container">
          <iframe 
            src={pathIframe} 
            title="Agenda deportiva especial"
            loading="lazy"
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
      )}
      
      {/* Renderizado de deportes */}
      {filteredTabData.sports.map((sport, sportIndex) => {
        // Solo renderizar deportes que tengan ligas con contenido
        const hasValidLeagues = sport.leagues && 
                               Array.isArray(sport.leagues) && 
                               sport.leagues.length > 0;
        
        return hasValidLeagues && (
          <SportContentHomeRender 
            key={`${sport.sport_code || sport.name || `sport-${sportIndex}`}-${index}-${sportIndex}`}
            sport={sport} 
            pathIframe={pathIframe}
            tabIndex={index}
            mostrarBotonApostar={mostrarBotonApostar}
            mostrarMomios={mostrarMomios}
            patrocinador={patrocinador}
            selectedLeague={selectedLeague}
          />
        );
      })}
    </>
  );
}